import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isSameDay } from 'date-fns';
import {
  getWeeklyTimesheet, upsertTimesheetEntry, deleteTimesheetEntry, getTasks, getStaff,
  lockWeek, unlockWeek, updateTimesheetDescription,
} from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateDdMmmYy } from '../../utils/format';

/**
 * The phone timesheet.
 *
 * The desktop screen is a task × 7-day grid — 8 columns of number boxes. That
 * shape cannot survive 375px: the old md:hidden fallback wrapped the week into
 * a 4-wide grid of ~30px inputs, which is both an odd reading of a week and
 * below any sane touch target. So the phone pivots the grid: pick one day,
 * then log against each task with a full-size input. Same state model, same
 * endpoints — only the axis you look down changes.
 */

interface Task { id: number; taskId: string; taskName: string; client?: { clientName: string }; category?: { name: string }; }
interface Staff { id: number; staffName: string; }
interface TimesheetEntry {
  id: number; staffId: number; taskId: number; date: string;
  hoursSpent: number; description?: string | null;
  task: { id: number; taskId: string; taskName: string; };
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const MobileTimesheet: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [dayIndex, setDayIndex] = useState(() => {
    const d = (new Date().getDay() + 6) % 7; // JS Sunday=0 → our Monday=0 week
    return d;
  });
  const [entries, setEntries] = useState<TimesheetEntry[]>([]);
  const [isLocked, setIsLocked] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState<number | undefined>(user?.staffId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rowTaskIds, setRowTaskIds] = useState<number[]>([]);
  const [taskSearch, setTaskSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [warnings, setWarnings] = useState<Record<number, string>>({});
  const [lockMsg, setLockMsg] = useState('');
  const [descriptions, setDescriptions] = useState<Record<number, string>>({});
  const [savedDescriptions, setSavedDescriptions] = useState<Record<number, string>>({});
  // Unsaved edits: key = `${taskId}-${dayIndex}`, value = the string typed.
  const [localHours, setLocalHours] = useState<Record<string, string>>({});

  const weekStartStr = format(weekStart, 'yyyy-MM-dd');
  const readOnly = isLocked && !isAdmin;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tsRes, taskRes] = await Promise.all([
        getWeeklyTimesheet(selectedStaffId, weekStartStr),
        getTasks(),
      ]);
      const tsData = tsRes.data;
      const entriesArr: TimesheetEntry[] = Array.isArray(tsData) ? tsData : (tsData.entries || []);
      setEntries(entriesArr);
      setIsLocked(Array.isArray(tsData) ? false : (tsData.isLocked || false));
      setTasks(taskRes.data);
      const seen = new Set<number>();
      const uniqueTaskIds: number[] = [];
      const descMap: Record<number, string> = {};
      entriesArr.forEach((e) => {
        if (!seen.has(e.taskId)) { seen.add(e.taskId); uniqueTaskIds.push(e.taskId); }
        if (e.description && !descMap[e.taskId]) descMap[e.taskId] = e.description;
      });
      setRowTaskIds(uniqueTaskIds);
      setDescriptions(descMap);
      setSavedDescriptions(descMap);
      setLocalHours({});
      setWarnings({});
    } finally {
      setLoading(false);
    }
  }, [selectedStaffId, weekStartStr]);

  useEffect(() => {
    if (isAdmin) getStaff().then((r) => setStaff(r.data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getDayDate = useCallback((i: number) => addDays(weekStart, i), [weekStart]);

  const getEntry = useCallback((taskId: number, i: number) => {
    const date = format(addDays(weekStart, i), 'yyyy-MM-dd');
    return entries.find((e) => e.taskId === taskId && e.date.slice(0, 10) === date);
  }, [entries, weekStart]);

  const getCurrentHoursNum = useCallback((taskId: number, i: number): number => {
    const key = `${taskId}-${i}`;
    if (key in localHours) {
      const v = parseInt(localHours[key], 10);
      return isNaN(v) || v < 0 ? 0 : v;
    }
    const entry = getEntry(taskId, i);
    return entry ? Number(entry.hoursSpent) : 0;
  }, [localHours, getEntry]);

  const getDisplayHours = (taskId: number, i: number): string => {
    const key = `${taskId}-${i}`;
    if (key in localHours) return localHours[key];
    const entry = getEntry(taskId, i);
    return entry ? String(entry.hoursSpent) : '';
  };

  const isDirty = useMemo(() =>
    Object.keys(localHours).length > 0 ||
    rowTaskIds.some((tid) => (descriptions[tid] || '') !== (savedDescriptions[tid] || '')),
    [localHours, descriptions, savedDescriptions, rowTaskIds]);

  const hasErrors = useMemo(() =>
    Object.values(warnings).some((w) => !w.startsWith('⚠')),
    [warnings]);

  const getDayTotal = useCallback((i: number) =>
    rowTaskIds.reduce((sum, taskId) => sum + getCurrentHoursNum(taskId, i), 0),
    [rowTaskIds, getCurrentHoursNum]);

  const getRowTotal = (taskId: number) =>
    DAYS.reduce((sum, _, i) => sum + getCurrentHoursNum(taskId, i), 0);

  const weekTotal = DAYS.reduce((sum, _, i) => sum + getDayTotal(i), 0);

  const validateHours = (value: string, taskId: number, i: number): string | null => {
    const parsed = parseFloat(value);
    if (isNaN(parsed) || parsed < 0) return 'Invalid hours';
    if (!Number.isInteger(parsed)) return 'Only whole numbers allowed';
    if (parsed > 24) return 'Cannot exceed 24 hours/day';
    const dayTotal = rowTaskIds.reduce((sum, tid) => (
      tid === taskId ? sum + parsed : sum + getCurrentHoursNum(tid, i)
    ), 0);
    if (dayTotal > 24) return `Day total (${dayTotal}h) exceeds 24h`;
    return null;
  };

  // Warning keys match the desktop scheme so the two screens stay comparable.
  const warnKey = (taskId: number, i: number) => i * 1000 + taskId;

  const handleHoursChange = (taskId: number, i: number, value: string) => {
    if (readOnly) return;
    const next = { ...warnings };
    if (value !== '' && !isNaN(parseInt(value, 10))) {
      const error = validateHours(value, taskId, i);
      if (error) next[warnKey(taskId, i)] = error;
      else {
        const parsed = parseInt(value, 10);
        if (parsed > 12) next[warnKey(taskId, i)] = `⚠ ${parsed}h seems high`;
        else delete next[warnKey(taskId, i)];
      }
    } else {
      delete next[warnKey(taskId, i)];
    }
    setWarnings(next);
    setLocalHours((prev) => ({ ...prev, [`${taskId}-${i}`]: value }));
  };

  const getTaskLabel = (taskId: number) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return `Task #${taskId}`;
    return `${task.taskId} — ${task.taskName}`;
  };

  const getTaskSub = (taskId: number) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return '';
    return [task.client?.clientName, task.category?.name].filter(Boolean).join(' · ');
  };

  const handleSave = async () => {
    for (const taskId of rowTaskIds) {
      const hasHours = DAYS.some((_, i) => getCurrentHoursNum(taskId, i) > 0);
      if (hasHours && !(descriptions[taskId] || '').trim()) {
        alert(`Please add a description for "${getTaskLabel(taskId)}" before saving.`);
        return;
      }
    }
    setSaving(true);
    try {
      for (const [key, value] of Object.entries(localHours)) {
        const [tid, di] = key.split('-');
        const taskId = parseInt(tid, 10);
        const i = parseInt(di, 10);
        const date = format(getDayDate(i), 'yyyy-MM-dd');
        const parsed = parseInt(value, 10);
        const existing = getEntry(taskId, i);
        if (value === '' || isNaN(parsed) || parsed <= 0) {
          if (existing) await deleteTimesheetEntry(existing.id);
        } else {
          await upsertTimesheetEntry({
            staffId: selectedStaffId, taskId, date, hoursSpent: parsed,
            description: (descriptions[taskId] || '').trim(),
          });
        }
      }
      for (const taskId of rowTaskIds) {
        const currentDesc = (descriptions[taskId] || '').trim();
        if (currentDesc !== (savedDescriptions[taskId] || '').trim() && entries.some((e) => e.taskId === taskId)) {
          await updateTimesheetDescription({ staffId: selectedStaffId, taskId, weekStart: weekStartStr, description: currentDesc });
        }
      }
      setLocalHours({});
      await fetchData();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Error saving timesheet');
    } finally {
      setSaving(false);
    }
  };

  const confirmDiscard = () => !isDirty || window.confirm('You have unsaved changes. Discard them?');

  const goWeek = (next: Date) => {
    if (!confirmDiscard()) return;
    setWeekStart(next);
  };

  const handleLockToggle = async () => {
    try {
      if (isLocked) {
        await unlockWeek(weekStartStr);
        setLockMsg(`Week of ${formatDateDdMmmYy(weekStart)} unlocked`);
      } else {
        await lockWeek(weekStartStr);
        setLockMsg(`Week of ${formatDateDdMmmYy(weekStart)} locked`);
      }
      await fetchData();
      setTimeout(() => setLockMsg(''), 4000);
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Error toggling lock');
    }
  };

  const addTaskRow = (task: Task) => {
    if (!rowTaskIds.includes(task.id)) setRowTaskIds([...rowTaskIds, task.id]);
    setTaskSearch('');
    setAdding(false);
  };

  const removeRow = (taskId: number) => {
    setRowTaskIds((prev) => prev.filter((id) => id !== taskId));
    setLocalHours((prev) => {
      const next = { ...prev };
      DAYS.forEach((_, i) => { delete next[`${taskId}-${i}`]; });
      return next;
    });
  };

  const filteredTaskOptions = tasks
    .filter((t) => !rowTaskIds.includes(t.id))
    .filter((t) => {
      if (!taskSearch) return true;
      const q = taskSearch.toLowerCase();
      return (
        t.taskId.toLowerCase().includes(q) ||
        t.taskName.toLowerCase().includes(q) ||
        (t.client?.clientName || '').toLowerCase().includes(q) ||
        (t.category?.name || '').toLowerCase().includes(q)
      );
    })
    .slice(0, 30);

  const staffName = isAdmin ? staff.find((s) => s.id === selectedStaffId)?.staffName : user?.staffName;
  const selectedDate = getDayDate(dayIndex);
  const thisWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const isThisWeek = isSameDay(weekStart, thisWeekStart);

  return (
    <div className="space-y-4 pb-2">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Timesheet</h1>
        {staffName && <p className="text-sm text-gray-500 mt-0.5">{staffName}</p>}
      </div>

      {isAdmin && (
        <select
          className="w-full h-11 px-3 border border-gray-200 rounded-xl bg-white text-sm"
          value={selectedStaffId || ''}
          onChange={(e) => { if (!confirmDiscard()) return; setSelectedStaffId(Number(e.target.value)); }}
        >
          <option value="">— Select Staff —</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.staffName}</option>)}
        </select>
      )}

      <div className="flex items-center gap-2">
        <button
          aria-label="Previous week"
          onClick={() => goWeek(subWeeks(weekStart, 1))}
          className="w-11 h-11 shrink-0 rounded-xl border border-gray-200 bg-white text-gray-600 text-lg active:bg-gray-100"
        >
          ‹
        </button>
        <button
          onClick={() => goWeek(thisWeekStart)}
          disabled={isThisWeek}
          className="flex-1 min-w-0 h-11 rounded-xl border border-gray-200 bg-white px-2 active:bg-gray-100 disabled:active:bg-white"
        >
          <span className="block text-xs font-semibold text-gray-800 truncate">
            {formatDateDdMmmYy(weekStart)} – {formatDateDdMmmYy(addDays(weekStart, 6))}
          </span>
          <span className="block text-[11px] text-gray-400">
            {isThisWeek ? 'This week' : 'Tap for this week'}
          </span>
        </button>
        <button
          aria-label="Next week"
          onClick={() => goWeek(addWeeks(weekStart, 1))}
          className="w-11 h-11 shrink-0 rounded-xl border border-gray-200 bg-white text-gray-600 text-lg active:bg-gray-100"
        >
          ›
        </button>
      </div>

      {isLocked && (
        <div className={`rounded-xl px-3 py-2.5 text-xs ${isAdmin ? 'bg-orange-50 border border-orange-200 text-orange-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          🔒 This week is locked — backdating is not allowed.
          {isAdmin && ' As Admin you can still edit and unlock.'}
        </div>
      )}

      {lockMsg && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-3 py-2 text-xs text-green-700">{lockMsg}</div>
      )}

      {/* Day strip. Seven fixed columns rather than a scroller — a whole week
          has to be visible at once for the totals to mean anything. */}
      <div className="grid grid-cols-7 gap-1">
        {DAYS.map((day, i) => {
          const total = getDayTotal(i);
          const active = i === dayIndex;
          const today = isSameDay(getDayDate(i), new Date());
          return (
            <button
              key={day}
              onClick={() => setDayIndex(i)}
              className={`h-14 rounded-xl border flex flex-col items-center justify-center leading-tight ${
                active ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-600 active:bg-gray-100'
              }`}
            >
              <span className={`text-[10px] ${active ? 'text-blue-100' : today ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
                {day.slice(0, 3)}
              </span>
              <span className="text-sm font-semibold">{format(getDayDate(i), 'dd')}</span>
              <span className={`text-[10px] ${active ? 'text-blue-100' : total > 0 ? 'text-blue-600' : 'text-gray-300'}`}>
                {total > 0 ? `${total}h` : '·'}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-gray-800">
          {DAYS[dayIndex]}, {formatDateDdMmmYy(selectedDate)}
        </h2>
        <span className={`text-xs font-semibold ${getDayTotal(dayIndex) > 12 ? 'text-orange-500' : 'text-blue-700'}`}>
          {getDayTotal(dayIndex)} hrs
        </span>
      </div>

      <div className="space-y-3">
        {loading ? (
          <p className="text-center text-sm text-gray-400 py-8">Loading…</p>
        ) : rowTaskIds.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-8 bg-white border border-gray-200 rounded-xl px-4">
            No tasks on this week's sheet yet. Add one below.
          </p>
        ) : (
          rowTaskIds.map((taskId) => {
            const warn = warnings[warnKey(taskId, dayIndex)];
            const isEdited = `${taskId}-${dayIndex}` in localHours;
            const sub = getTaskSub(taskId);
            return (
              <div key={taskId} className="bg-white border border-gray-200 rounded-xl p-3">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{getTaskLabel(taskId)}</p>
                    {sub && <p className="text-xs text-gray-500 truncate mt-0.5">{sub}</p>}
                    <p className="text-[11px] text-gray-400 mt-0.5">Week total: {getRowTotal(taskId)} hrs</p>
                  </div>
                  <input
                    type="number"
                    min="0"
                    max="24"
                    step="1"
                    // Whole hours only (validateHours rejects fractions), so the
                    // numeric keypad is the right one — not the decimal pad.
                    inputMode="numeric"
                    value={getDisplayHours(taskId, dayIndex)}
                    disabled={readOnly}
                    onChange={(e) => handleHoursChange(taskId, dayIndex, e.target.value)}
                    placeholder="0"
                    aria-label={`Hours for ${getTaskLabel(taskId)}`}
                    className={`w-16 h-11 shrink-0 text-center text-base font-semibold border rounded-xl focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:text-gray-400 ${
                      warn && !warn.startsWith('⚠') ? 'border-red-400 bg-red-50 focus:ring-red-400'
                        : warn ? 'border-yellow-400 bg-yellow-50 focus:ring-yellow-400'
                        : isEdited ? 'border-blue-300 bg-blue-50 focus:ring-blue-500'
                        : 'border-gray-200 focus:ring-blue-500'
                    }`}
                  />
                </div>

                {warn && (
                  <p className={`text-[11px] mt-1.5 ${warn.startsWith('⚠') ? 'text-yellow-700' : 'text-red-600'}`}>{warn}</p>
                )}

                {/* Description is per task per week, not per day — editing it
                    here changes it for the whole row, same as desktop. */}
                <input
                  type="text"
                  className="w-full h-11 mt-2 px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                  placeholder="Description (required)"
                  value={descriptions[taskId] || ''}
                  onChange={(e) => setDescriptions((prev) => ({ ...prev, [taskId]: e.target.value }))}
                  disabled={readOnly}
                />

                {!readOnly && (
                  <button
                    onClick={() => removeRow(taskId)}
                    className="mt-2 h-9 px-3 -ml-1 text-xs text-gray-500 active:text-red-600"
                  >
                    Remove from week
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {!readOnly && (
        <div>
          {!adding ? (
            <button
              onClick={() => setAdding(true)}
              className="w-full h-11 rounded-xl border border-dashed border-gray-300 bg-white text-sm font-medium text-blue-700 active:bg-gray-50"
            >
              + Add task
            </button>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  autoFocus
                  className="flex-1 min-w-0 h-11 px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search task, client, category…"
                  value={taskSearch}
                  onChange={(e) => setTaskSearch(e.target.value)}
                />
                <button
                  onClick={() => { setAdding(false); setTaskSearch(''); }}
                  className="h-11 px-3 shrink-0 text-sm text-gray-500"
                >
                  Cancel
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                {filteredTaskOptions.length === 0 ? (
                  <p className="text-sm text-gray-400 py-4 text-center">No matching tasks</p>
                ) : (
                  filteredTaskOptions.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => addTaskRow(t)}
                      className="w-full text-left py-2.5 px-1 active:bg-blue-50"
                    >
                      <span className="block text-sm font-medium text-gray-800 truncate">{t.taskId} — {t.taskName}</span>
                      <span className="block text-xs text-gray-400 truncate mt-0.5">
                        {t.client?.clientName || 'No client'}{t.category?.name ? ` · ${t.category.name}` : ''}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <button
          onClick={handleLockToggle}
          className={`w-full h-11 rounded-xl text-sm font-medium border ${
            isLocked ? 'bg-red-50 text-red-700 border-red-200' : 'bg-white text-gray-600 border-gray-200'
          }`}
        >
          {isLocked ? '🔒 Locked — tap to unlock week' : '🔓 Open — tap to lock week'}
        </button>
      )}

      {/* Sticky inside the shell's scrolling <main>; the bleed cancels its
          p-4 gutters so the bar spans the full width. */}
      {!readOnly && (
        <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-white border-t border-gray-200 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500">Week total</p>
            <p className="text-base font-bold text-blue-700 leading-tight">{weekTotal} hrs</p>
          </div>
          <button
            onClick={handleSave}
            disabled={!isDirty || saving || hasErrors}
            className={`h-11 px-5 rounded-xl text-sm font-semibold ${
              isDirty && !hasErrors && !saving
                ? 'bg-blue-600 text-white active:bg-blue-700'
                : 'bg-gray-100 text-gray-400'
            }`}
          >
            {saving ? 'Saving…' : isDirty ? 'Save week' : 'Saved'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MobileTimesheet;
